import type { ReactNode } from 'react';

/**
 * قواعد bidi الملزمة (frontend-design §3.2). هذا الملف هو **التنفيذ الوحيد**
 * للقواعد 1 و2 و3 و5؛ `src/lib/bidi.tsx` مجرد مسار استيراد بديل يعيد التصدير منه.
 *
 * القاعدة 1: كل نص أدخله مستخدم أو وكيل (عناوين، raw_text، body_md، waiting_on،
 * أسماء tags) يُعرض حصرًا عبر هذه المكوّنات — عرضه خامًا يُرفض في مراجعة الكود.
 */

export interface UserTextProps {
  children: ReactNode;
  /** القاعدة 3: كتلة متعددة الأسطر — حاوية dir="auto" وكل سطر يُحسب على حده. */
  block?: boolean;
  className?: string;
}

/**
 * `block=false` → القاعدة 2: `<bdi>` سطري داخل جملة الواجهة (عزل بلا كسر التدفق).
 * `block=true`  → القاعدة 3: حاوية `dir="auto"` مع `unicode-bidi: plaintext`.
 */
export function UserText({ children, block = false, className }: UserTextProps) {
  if (block) {
    return (
      <div dir="auto" className={joinClass('bidi-plaintext text-start', className)}>
        {children}
      </div>
    );
  }
  return <bdi className={className}>{children}</bdi>;
}

export interface UserSpanProps {
  children: ReactNode;
  className?: string;
}

/**
 * القاعدة 2 صراحةً: النص السطري داخل جملة واجهة — "أضافه Hermes إلى <bdi>…</bdi>".
 * `<bdi>` يحمل `unicode-bidi: isolate` من المتصفح، فلا نكرره في CSS.
 */
export function UserSpan({ children, className }: UserSpanProps) {
  return <bdi className={className}>{children}</bdi>;
}

/**
 * القاعدة 5: الأرقام والمبالغ والأكواد والمعرفات والمسارات — LTR معزول بأرقام جدولية.
 */
export function LtrText({ children, className }: UserSpanProps) {
  return (
    <span dir="ltr" className={joinClass('bidi-isolate tabular-nums', className)}>
      {children}
    </span>
  );
}

function joinClass(base: string, extra?: string): string {
  return extra === undefined || extra === '' ? base : `${base} ${extra}`;
}
